import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { map } from 'rxjs'

import { FlxRadioThemeDirective, FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxRadioModule } from '@flowx/angular-ui-toolkit'
import {
  FormFieldsEnum,
  LabelAccessibilityProps,
  RadioTemplateConfig,
  prototypeInstanceMetaToPath,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxFieldOptionService } from '../../services/flx-field-option.service'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-radio-wrapper',
  imports: [
    CommonModule,
    FlxRadioModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  hostDirectives: [FlxRadioThemeDirective],
  templateUrl: './radio-wrapper.component.html',
  styleUrl: './radio-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxRadioWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #optionsService = inject(FlxFieldOptionService)

  templateConfig = input.required<RadioTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    selected: getThemeIcon(ThemeComponent.Radio, 'selectedIcon'),
    unselected: getThemeIcon(ThemeComponent.Radio, 'unselectedIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  fieldKey = computed(() => {
    const { key, prototypeInstanceMeta } = this.templateConfig()
    return prototypeInstanceMeta
      ? `${prototypeInstanceMetaToPath(prototypeInstanceMeta)}.${key}`
      : key
  })

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  options: Signal<Array<{ label: string; value: string; enabled?: boolean }>>
  ariaLabel: Signal<string>

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, helper } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
      this.options = toSignal(
        this.#optionsService
          .getOptionsObs(config, this.control().parent as FormGroup, FormFieldsEnum.RADIO)
          .pipe(
            map((options) =>
              options.map((option) => ({
                label: option.label,
                value: option.value,
                enabled: option.enabled !== undefined ? option.enabled : true,
              }))
            )
          )
      )
    })
  }
}
